import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * The device copy of "this person finished onboarding". The account record
 * (profiles.onboarding_completed_at) is the source of truth; this flag is a
 * fast local cache of it. Every write goes through markOnboardingCompleteOnDevice
 * so screens that read it (the bottom menu) update right away without a reload.
 */
export const ONBOARDING_COMPLETE_KEY = "grounded_onboarding_complete";
export const ONBOARDING_IN_PROGRESS_KEY = "grounded_onboarding_in_progress";
const CHANGE_EVENT = "grounded:onboarding-flag";

export function isOnboardingCompleteOnDevice(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "true";
  } catch {
    return false;
  }
}

export function markOnboardingCompleteOnDevice(): void {
  try {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
    localStorage.removeItem(ONBOARDING_IN_PROGRESS_KEY);
  } catch { /* ignore */ }
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch { /* ignore */ }
}

function subscribe(cb: () => void) {
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

export function useOnboardingCompleteOnDevice(): boolean {
  return useSyncExternalStore(subscribe, isOnboardingCompleteOnDevice, () => false);
}

/** Onboarding is on screen (address "/") or reached its account step. */
export function isInsideOnboarding(): boolean {
  try {
    if (localStorage.getItem(ONBOARDING_IN_PROGRESS_KEY)) return true;
  } catch { /* ignore */ }
  return typeof window !== "undefined" && window.location.pathname === "/";
}

/**
 * Rule 3d: on a real sign in, an account that already finished onboarding gets
 * the device flag too (new phone, reinstall). One read per account per page
 * load; sign in screens await it before navigating so the flag is in place
 * before the person lands in the app.
 */
const inflight = new Map<string, Promise<void>>();

export function syncOnboardingFlagFromAccount(userId: string): Promise<void> {
  if (isOnboardingCompleteOnDevice()) return Promise.resolve();
  const existing = inflight.get(userId);
  if (existing) return existing;
  const p = new Promise<{ data: unknown; error: unknown }>((resolve, reject) => {
    try {
      Promise.resolve(
        supabase
          .from("profiles")
          .select("onboarding_completed_at")
          .eq("id", userId)
          .maybeSingle(),
      ).then(resolve, reject);
    } catch (e) {
      reject(e);
    }
  })
    .then(async ({ data, error }) => {
      if (error || !data) return;
      if ((data as { onboarding_completed_at: string | null }).onboarding_completed_at) {
        markOnboardingCompleteOnDevice();
        return;
      }
      // Item 4: a sign in outside onboarding marks the account finished
      // (fill null only) and sets the device flag. Inside onboarding the flow
      // owns the finish moment, so nothing is written here.
      if (isInsideOnboarding()) return;
      const { error: stampError } = await supabase
        .from("profiles")
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq("id", userId)
        .is("onboarding_completed_at", null);
      if (!stampError) markOnboardingCompleteOnDevice();
    })
    .catch(() => { /* unknown: leave the flag alone */ })
    .finally(() => {
      inflight.delete(userId);
    });
  inflight.set(userId, p);
  return p;
}

/** Same as syncOnboardingFlagFromAccount, but never waits longer than `ms`. */
export function waitForOnboardingFlagSync(userId: string, ms = 2500): Promise<void> {
  return Promise.race([
    syncOnboardingFlagFromAccount(userId),
    new Promise<void>((r) => setTimeout(r, ms)),
  ]);
}
